/**
 * Video Component for Puck Editor
 * 
 * Embed video dari YouTube atau URL langsung
 */

import type { ComponentConfig } from '@measured/puck'

export interface VideoProps {
  src: string
  type: 'youtube' | 'direct'
  aspectRatio: '16:9' | '4:3' | '1:1'
  rounded: 'none' | 'small' | 'medium' | 'large'
  autoplay: boolean
  muted: boolean
}

const aspectRatioClasses = {
  '16:9': 'aspect-video',
  '4:3': 'aspect-[4/3]',
  '1:1': 'aspect-square',
}

const roundedClasses = {
  none: 'rounded-none',
  small: 'rounded',
  medium: 'rounded-lg',
  large: 'rounded-xl',
}

// Extract YouTube video ID from various URL formats
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

export function Video({
  src,
  type,
  aspectRatio,
  rounded,
  autoplay,
  muted,
}: VideoProps) {
  if (!src) {
    return (
      <div
        className={`bg-gray-200 flex items-center justify-center ${aspectRatioClasses[aspectRatio]} ${roundedClasses[rounded]}`}
      >
        <span className="text-gray-400">Tambahkan URL video</span>
      </div>
    )
  }

  if (type === 'youtube') {
    const videoId = getYouTubeId(src)
    if (!videoId) {
      return (
        <div
          className={`bg-red-100 flex items-center justify-center ${aspectRatioClasses[aspectRatio]} ${roundedClasses[rounded]}`}
        >
          <span className="text-red-500">URL YouTube tidak valid</span>
        </div>
      )
    }

    const params = new URLSearchParams()
    if (autoplay) params.set('autoplay', '1')
    if (muted) params.set('mute', '1')
    params.set('rel', '0')

    return (
      <div className={`${aspectRatioClasses[aspectRatio]} ${roundedClasses[rounded]} overflow-hidden`}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?${params.toString()}`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video"
        />
      </div>
    )
  }

  // Direct video URL
  return (
    <div className={`${aspectRatioClasses[aspectRatio]} ${roundedClasses[rounded]} overflow-hidden`}>
      <video
        src={src}
        className="w-full h-full object-cover"
        controls
        autoPlay={autoplay}
        muted={muted}
        playsInline
      />
    </div>
  )
}

export const VideoConfig: ComponentConfig<VideoProps> = {
  label: 'Video',
  defaultProps: {
    src: '',
    type: 'youtube',
    aspectRatio: '16:9',
    rounded: 'medium',
    autoplay: false,
    muted: false,
  },
  fields: {
    src: {
      type: 'text',
      label: 'URL Video',
    },
    type: {
      type: 'radio',
      label: 'Jenis',
      options: [
        { label: 'YouTube', value: 'youtube' },
        { label: 'Langsung', value: 'direct' },
      ],
    },
    aspectRatio: {
      type: 'select',
      label: 'Rasio Aspek',
      options: [
        { label: '16:9 (Widescreen)', value: '16:9' },
        { label: '4:3 (Standard)', value: '4:3' },
        { label: '1:1 (Square)', value: '1:1' },
      ],
    },
    rounded: {
      type: 'select',
      label: 'Sudut',
      options: [
        { label: 'Tidak Ada', value: 'none' },
        { label: 'Kecil', value: 'small' },
        { label: 'Sedang', value: 'medium' },
        { label: 'Besar', value: 'large' },
      ],
    },
    autoplay: {
      type: 'radio',
      label: 'Autoplay',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
    muted: {
      type: 'radio',
      label: 'Muted',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
  },
  render: Video,
}
